import type { AppState } from './default-state';
import { createDefaultState } from './default-state';
import type { Command } from './commands';
import { normalizeMeanAnomaly } from './phase';
import { MIN_SECTOR_COUNT, MAX_SECTOR_COUNT } from '../core/sectors';
import {
  ECCENTRICITY_MIN,
  ECCENTRICITY_MAX,
  SPEED_MIN_DAYS_PER_SECOND,
  SPEED_MAX_DAYS_PER_SECOND
} from '../core/ranges';
import {
  FIT_VIEW_CAMERA,
  applyZoom,
  applyPanWorld,
  applyPanStep,
  maxApoapsisMetresForState,
  type ViewCamera
} from './camera';

/**
 * Pure reducer (AD-2). Given the current state and a command, returns the next
 * state. The sim clock is an injected `dt` in SI seconds and is not read here;
 * no Date access in reducers.
 *
 * `Reset` restores the full default system in one command (nine bodies, Sun
 * centre, Earth selected, time zero, default speed and legend order); a calm
 * recovery action, never destructive.
 *
 * Story 1.3 adds two named verbs:
 *   - `SelectBody`: set the session-only `selectedBody`; change nothing else.
 *   - `SetEccentricity`: clamp `value` to [0, 0.95]; no-op when the target is the
 *     current central body (AC 7); otherwise write ONLY the target body's live
 *     `e`, leaving `a`, period inputs, and every other body untouched.
 *
 * Story 1.4 adds `SetSemiMajorAxis`: write ONLY the target body's live SI `a`
 * (metres), REJECTING (returning state unchanged, never clamping) a non-finite or
 * non-positive value, the central body, or a removed body, so the last valid value
 * stays active (AC 6). The ui owns the 0.01-100 AU range check (AD-5).
 *
 * Story 2.2 adds `SwitchCentre`: set `centralBody` to the target (a no-op when it
 * is already the centre or the target is a removed body), and transfer the new
 * centre's former semi-major axis AND eccentricity to the previous centre, which
 * becomes an orbiter. Every other body, and every session field, is untouched;
 * period recomputation is automatic through the selectors' central-mu read.
 *
 * Story 3.1 adds `ReorderLegend`: write ONLY `state.legendOrder` when the payload
 * is an exact permutation of the current legend ids; reject any invalid or
 * already-identical order as a referential no-op (keeping the last valid order).
 * This is the last verb: after it no command case throws.
 */

// Editable e and speed ranges now come from the single canonical source
// `core/ranges.ts` (imported above) so the Story 5.1 share codec's clamps are
// PROVABLY these reducer clamps (AD-2/AD-11 anti-drift): no range literal is
// duplicated across the `state`/`share` boundary.

/** True when two cameras are field-equal (drives the same-reference no-op). */
function sameCamera(a: ViewCamera, b: ViewCamera): boolean {
  return a.zoom === b.zoom && a.panX === b.panX && a.panY === b.panY;
}

export function reduce(state: AppState, command: Command): AppState {
  switch (command.type) {
    case 'Reset': {
      // Reset restores the full default SIMULATED system (nine bodies, Sun centre,
      // Earth selected, time zero, default speed and legend order). Graphics profile
      // and reduced-effects are DEVICE-LOCAL PREFERENCES (AD-7), not part of the
      // simulated experiment, so they are PRESERVED across Reset rather than
      // clobbered: a calm recovery of the experiment must not silently change the
      // learner's chosen graphics mode or the concrete adapter that is mounted.
      const fresh = createDefaultState();
      return {
        ...fresh,
        requestedProfile: state.requestedProfile,
        reducedEffects: state.reducedEffects,
        activeRenderMode: state.activeRenderMode,
        unavailableFeatures: state.unavailableFeatures
      };
    }
    case 'SelectBody':
      // Selection is distinct from central treatment; selecting the central body
      // is allowed and drives the AC-7 read-only case. No orbital state changes.
      if (state.selectedBody === command.body) return state;
      return { ...state, selectedBody: command.body };
    case 'SetEccentricity': {
      // Non-finite input is ignored (defensive; the UI never sends it).
      if (!Number.isFinite(command.value)) return state;
      // AC 7: the central body does not orbit; editing its e is a no-op.
      if (command.body === state.centralBody) return state;
      // Story 4.1 (AC 5): a removed (collided/absorbed) body cannot be edited as an
      // active orbiter; mirror the SetSemiMajorAxis non-active guard.
      const eTarget = state.bodies.find((b) => b.id === command.body);
      if (!eTarget || eTarget.status !== 'active') return state;
      const clamped = Math.min(ECCENTRICITY_MAX, Math.max(ECCENTRICITY_MIN, command.value));
      const bodies = state.bodies.map((b) =>
        b.id === command.body ? { ...b, e: clamped } : b
      );
      return { ...state, bodies };
    }
    case 'SetSemiMajorAxis': {
      // `value` is SI metres (the AU conversion + the 0.01-100 AU range check
      // happen once at the ui boundary, AD-5). REJECT, NEVER CLAMP (AC 6): a
      // non-finite or non-positive value, a target that is the current central
      // body (AC 7), or a removed body leaves state unchanged so the last valid
      // value stays active. Clamping would apply a different value.
      if (!Number.isFinite(command.value) || command.value <= 0) return state;
      if (command.body === state.centralBody) return state;
      const target = state.bodies.find((b) => b.id === command.body);
      if (!target || target.status !== 'active') return state;
      const bodies = state.bodies.map((b) =>
        b.id === command.body ? { ...b, a: command.value } : b
      );
      return { ...state, bodies };
    }
    case 'Play':
      // Transport only: begin advancing on the next injected tick. Changes no
      // orbital parameter, body, centre, selection, speed, or time (AC 2).
      if (!state.paused) return state;
      return { ...state, paused: false };
    case 'Pause':
      // Transport only: halt advancing; the ticker's advanceTime becomes a no-op.
      // Changes no orbital parameter (AC 3).
      if (state.paused) return state;
      return { ...state, paused: true };
    case 'SetSpeed': {
      // Clamp a finite days-per-second into [0.1, 365] inclusive (AC 4). A
      // non-finite value is ignored (state unchanged) so speed never becomes NaN;
      // clamping/ignoring, never throwing, matches the non-blocking errors
      // convention. `speedDaysPerSecond` stays a raw number in `state`; the
      // days/sec label is a ui readout-boundary concern (AD-5).
      if (!Number.isFinite(command.daysPerSecond)) return state;
      const clamped = Math.min(
        SPEED_MAX_DAYS_PER_SECOND,
        Math.max(SPEED_MIN_DAYS_PER_SECOND, command.daysPerSecond)
      );
      if (clamped === state.speedDaysPerSecond) return state;
      return { ...state, speedDaysPerSecond: clamped };
    }
    case 'SetGraphicsProfile':
      // Device-local preference. Changes ONLY the requested profile; the concrete
      // mounted mode is recorded separately via SetActiveRenderMode once the canvas
      // host resolves and mounts, so this never disturbs the running experiment.
      if (state.requestedProfile === command.profile) return state;
      return { ...state, requestedProfile: command.profile };
    case 'SetReducedEffects':
      // Independent axis from the graphics profile (Consistency Conventions).
      // Changes ONLY the toggle; orbital timing, calculations, and controls are
      // untouched (AC 6).
      if (state.reducedEffects === command.value) return state;
      return { ...state, reducedEffects: command.value };
    case 'SetActiveRenderMode': {
      // Records the concrete adapter that actually mounted (startup and after any
      // fallback), so the store never claims Enhanced when Compatibility is live
      // (AC 5). Preserves every simulated field (orbital parameters, sim time,
      // playback, selection) so a fallback never disturbs the experiment (AC 4).
      // Return the SAME reference when nothing changed so the reactive canvas host
      // does not re-run its rebuild effect on an idempotent record (avoids churn).
      const sameFeatures =
        state.unavailableFeatures.length === command.unavailableFeatures.length &&
        state.unavailableFeatures.every((f, i) => f === command.unavailableFeatures[i]);
      if (state.activeRenderMode === command.mode && sameFeatures) return state;
      return {
        ...state,
        activeRenderMode: command.mode,
        unavailableFeatures: [...command.unavailableFeatures]
      };
    }
    case 'NudgePhase': {
      // Direct-manipulation phase (Story 1.7, FR-19). Shift ONLY the target
      // body's dragged-phase offset by `deltaMeanAnomaly` (mean-anomaly radians),
      // wrapped into [0, 2*pi). Dragging/stepping SETS phase; it never advances
      // `time` and changes no orbital parameter, body, centre, selection, speed,
      // legend, or any other body's offset. Pure: no Date, no dt. A non-finite
      // delta is ignored so the offset never becomes NaN (non-blocking convention).
      if (!Number.isFinite(command.deltaMeanAnomaly)) return state;
      // Story 4.1 (AC 5): a removed body has no live phase to step; reject it (a
      // collided body cannot be nudged), mirroring the SetSemiMajorAxis guard.
      const phaseTarget = state.bodies.find((b) => b.id === command.body);
      if (!phaseTarget || phaseTarget.status !== 'active') return state;
      const current = state.phaseOffsets[command.body] ?? 0;
      const next = normalizeMeanAnomaly(current + command.deltaMeanAnomaly);
      return {
        ...state,
        phaseOffsets: { ...state.phaseOffsets, [command.body]: next }
      };
    }
    case 'SetSectorOverlayVisible':
      // Story 2.1 (FR-9): toggle the equal-time-sector overlay. Writes ONLY
      // `sectorOverlayVisible`; changes no orbital parameter, body, centre,
      // selection, time, speed, or sector count. Same reference when unchanged.
      if (state.sectorOverlayVisible === command.value) return state;
      return { ...state, sectorOverlayVisible: command.value };
    case 'SetSectorCount': {
      // Story 2.1 (FR-9, AC 4): CLAMP to the permitted integer range so values
      // outside [2, 24] cannot be applied. A non-finite value is ignored (state
      // unchanged, non-blocking convention); otherwise round then clamp. Clamp (not
      // reject) mirrors `SetSpeed` and the Story 5.1 share-decode rule. Changes ONLY
      // `sectorCount`; same reference when unchanged.
      if (!Number.isFinite(command.count)) return state;
      const clamped = Math.min(
        MAX_SECTOR_COUNT,
        Math.max(MIN_SECTOR_COUNT, Math.round(command.count))
      );
      if (clamped === state.sectorCount) return state;
      return { ...state, sectorCount: clamped };
    }
    case 'SwitchCentre': {
      // Story 2.2 (FR-06, FR-07). Make `command.body` the central body. The
      // PREVIOUS centre becomes an orbiter inheriting the NEW centre's former
      // semi-major axis AND eccentricity (epics/BDD acceptance, deliberately over
      // PRD 5.5's axis-only wording). Period recomputation is automatic: once
      // `centralBody` flips, `selectOrbitalReadouts` reads `getBody(centre).mu`,
      // so no arithmetic happens here. Pure: no Date, no dt.
      const newCentre = command.body;
      const prevCentre = state.centralBody;
      // AC 1: reselecting the current centre is not a state-changing action.
      if (newCentre === prevCentre) return state;
      // Defensive (mirrors SetSemiMajorAxis): a removed body can never be centred.
      const target = state.bodies.find((b) => b.id === newCentre);
      if (!target || target.status !== 'active') return state;
      // Capture the new centre's former live elements BEFORE mapping.
      const former = { a: target.a, e: target.e };
      // Transfer to the previous centre only; every other body (including the new
      // centre, which keeps its stored a/e for a reversible later switch) is
      // byte-identical.
      const bodies = state.bodies.map((b) =>
        b.id === prevCentre ? { ...b, a: former.a, e: former.e } : b
      );
      // Change centralBody and exactly one body's a/e; leave selectedBody, paused,
      // time, speed, legendOrder, phaseOffsets, sectors, and graphics untouched.
      return { ...state, centralBody: newCentre, bodies };
    }
    case 'ReorderLegend': {
      // Story 3.1 (FR-08). "Safely" means a pure presentation change: write ONLY
      // `legendOrder`, never a body, centre, selection, or any other field, so
      // state invariance (AC-5) is structural. Validate `command.order` is an
      // EXACT permutation of the store's own current legend ids (each present once,
      // correct length, no duplicate, no unknown id), compared against
      // `state.legendOrder` rather than a hard-coded list so a reference-data change
      // never silently breaks it. An invalid order is REJECTED as a referential
      // no-op (return `state`), never thrown/partially applied/repaired, mirroring
      // the SetSemiMajorAxis reject-and-keep-last-valid pattern and the non-blocking
      // errors convention. An already-identical order is also a same-reference no-op
      // so an idempotent reorder does not churn subscribers. Removed/status-flagged
      // bodies stay in the order (no status special-casing).
      const next = command.order;
      const ids = state.legendOrder;
      const set = new Set(ids);
      const isPermutation =
        next.length === ids.length &&
        next.every((id) => set.has(id)) &&
        new Set(next).size === next.length;
      if (!isPermutation) return state;
      const unchanged = next.every((id, i) => id === ids[i]);
      if (unchanged) return state;
      return { ...state, legendOrder: [...next] };
    }
    case 'ZoomView': {
      // Story 3.2 (FR-11). Fold a zoom modifier over the fit baseline (the
      // baseline itself is recomputed at render time in `composeCamera`). Writes
      // ONLY `state.camera`; changes no body, centre, selection, time, speed,
      // paused, legend, phase, sector, or graphics field (AD-9). A focus (from
      // wheel/pinch) keeps that world point fixed on screen; omitting it zooms
      // about the viewport centre. Same reference on a clamped/idempotent no-op.
      const next = applyZoom(state.camera, command.factor, command.focusX, command.focusY);
      return sameCamera(state.camera, next) ? state : { ...state, camera: next };
    }
    case 'PanView': {
      // Continuous world-metre pan from the projection-bearing pointer drag. The
      // clamp basis is maxApoapsis over the SAME active/non-central filter both
      // renderers use, so the reducer clamp and the render fit cannot diverge.
      const maxApo = maxApoapsisMetresForState(state.bodies, state.centralBody);
      const next = applyPanWorld(state.camera, command.dxWorld, command.dyWorld, maxApo);
      return sameCamera(state.camera, next) ? state : { ...state, camera: next };
    }
    case 'PanStep': {
      // Discrete, zoom-aware pan for the top-bar buttons and canvas arrow keys
      // (no projection needed — reducer-computed). Same clamp basis as PanView.
      const maxApo = maxApoapsisMetresForState(state.bodies, state.centralBody);
      const next = applyPanStep(state.camera, command.dir, maxApo);
      return sameCamera(state.camera, next) ? state : { ...state, camera: next };
    }
    case 'FitAllOrbits': {
      // The identity modifier: reset to fit-all (zoom 1, pan 0). The view then
      // reframes every active orbit through the recomputed baseline. Same
      // reference when already fit, to avoid render churn.
      if (sameCamera(state.camera, FIT_VIEW_CAMERA)) return state;
      return { ...state, camera: FIT_VIEW_CAMERA };
    }
    case 'SetScaleMode':
      // Story 3.3 (FR-12): switch the body-size scale mode. Writes ONLY
      // `state.scaleMode`; changes no orbital parameter, body, centre, selection,
      // time, speed, sector, camera, or graphics field. Scale mode is a pure
      // render concern (AD-1/AD-10): the reducer performs no core arithmetic and
      // no geometry is touched. Same reference when unchanged (mirror
      // `SetReducedEffects` / `SetSectorOverlayVisible`). NOT added to the Reset
      // preserve-list: it is shareable simulated config, so Reset restores the
      // `'readable'` factory default.
      if (state.scaleMode === command.mode) return state;
      return { ...state, scaleMode: command.mode };
    default: {
      // Exhaustiveness guard: a new command type must be handled explicitly.
      const _never: never = command;
      throw new Error(`Unhandled command: ${JSON.stringify(_never)}`);
    }
  }
}
